import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * „Z aplikace" — screens of the real admin (KAN-1329).
 *
 * The images are captures of the running app, not mock-ups: the section's
 * own lead promises "no fictional dashboards", and a drawn screen would make
 * that sentence the first lie on the page. They live in ``public/landing``
 * and are re-shot when the screens change; a missing file degrades to the
 * design's hatched placeholder instead of a broken-image icon.
 */
// Order follows the card widths of the design's flex row (the 2nd card is the
// narrowest, the 3rd the widest): the flow map needs its whole width to say
// "in parallel", the 3D hall reads from its centre in a narrow card.
const SHOTS = [
  { key: "pricing", tint: "t-blue", src: "/landing/app-cenik.webp", center: false },
  { key: "locations", tint: "t-slate", src: "/landing/app-mapa.webp", center: true },
  { key: "flow", tint: "t-teal", src: "/landing/app-retez.webp", center: true },
  { key: "deadlines", tint: "t-green", src: "/landing/app-terminy.webp", center: false },
  { key: "shipping", tint: "t-amber", src: "/landing/app-doprava.webp", center: false },
  { key: "mail", tint: "t-violet", src: "/landing/app-posta.webp", center: false },
  { key: "invoice", tint: "t-rose", src: "/landing/app-faktura.webp", center: false },
  { key: "machines", tint: "t-indigo", src: "/landing/app-stroje.webp", center: false },
] as const;

function ShotImage({ src, center }: { src: string; center: boolean }): JSX.Element {
  const [failed, setFailed] = useState(false);
  return (
    <div className={center ? "shot-img center" : "shot-img"}>
      {!failed && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

export function PlatformShots(): JSX.Element {
  const { t } = useTranslation("common");

  return (
    <section className="section collage-sec" data-testid="platform-shots">
      <div className="inner shots-wrap">
        <p className="kicker">{t("platformLanding.shots.kicker")}</p>
        <h2 className="h2" style={{ maxWidth: 620 }}>
          {t("platformLanding.shots.heading")}
        </h2>
        <p className="lead" style={{ maxWidth: 600 }}>
          {t("platformLanding.shots.lead")}
        </p>

        <ul className="shots">
          {SHOTS.map((shot) => (
            <li key={shot.key} className={`shot ${shot.tint}`}>
              <div className="shot-bar">
                <span aria-hidden="true" className="pip" />
                <em>{t(`platformLanding.shots.items.${shot.key}.tag`)}</em>
              </div>
              <ShotImage src={shot.src} center={shot.center} />
              <p className="shot-cap">
                <b>{t(`platformLanding.shots.items.${shot.key}.title`)}</b>{" "}
                {t(`platformLanding.shots.items.${shot.key}.body`)}
              </p>
            </li>
          ))}
          <li className="shot shot-more">
            <div>
              <b>{t("platformLanding.shots.more.title")}</b>
              <span>{t("platformLanding.shots.more.body")}</span>
            </div>
            <a className="more-link" href="#kontakt">
              {t("platformLanding.shots.more.cta")}
            </a>
          </li>
        </ul>
      </div>
    </section>
  );
}
